import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Chip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { toast } from "react-toastify";
import {
  useGetResourcesCategorizedMutation
} from "../../../app/services/resourceHubServices";
import { useGetAllResourceCategoriesQuery } from "../../../app/services/resourceCategoryServices";
import { formatDescription } from "../../../app/utils/utils";
import VideocamIcon from '@mui/icons-material/Videocam';


export default function CategorizedResourceView() {
  const navigate = useNavigate();
  const [fileType, setFileType] = useState('');
  const [categoryId, setCategoryId] = useState('');

  const [page, setPage] = useState(1);
  const [categorizedData, setCategorizedData] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const [getResources, { isLoading, isFetching }] = useGetResourcesCategorizedMutation();
  const { data: categoryData } = useGetAllResourceCategoriesQuery();

  const observer = useRef();

  const lastElementRef = useCallback(node => {
    if (isLoading || isFetching) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, isFetching, hasMore]);

  const loadResources = async (currentPage, isReset = false) => {
    try {
      const filters = {
        page: currentPage,
        itemsPerPage: 10
      };
      if (fileType) filters.fileType = fileType;
      if (categoryId) filters.resourceCategoryId = categoryId;

      const res = await getResources(filters).unwrap();

      if (res.status && res.data) {
        if (isReset) {
          setCategorizedData(res.data.categorisedResources || []);
        } else {
          setCategorizedData(prev => {
            const newArray = [...prev];
            const incoming = res.data.categorisedResources || [];

            incoming.forEach(incCat => {
              const existingCatIndex = newArray.findIndex(
                c => (c.categoryDetails?._id === incCat.categoryDetails?._id)
              );
              if (existingCatIndex > -1) {
                const existingCat = { ...newArray[existingCatIndex] };
                const existingResIds = new Set(existingCat.resources.map(r => r._id));
                const uniqueNewResources = incCat.resources.filter(r => !existingResIds.has(r._id));
                existingCat.resources = [...existingCat.resources, ...uniqueNewResources];
                newArray[existingCatIndex] = existingCat;
              } else {
                newArray.push(incCat);
              }
            });
            return newArray;
          });
        }

        setHasMore(currentPage < res.data.totalPages);
      }
    } catch (error) {
      if (error?.status !== 404) {
        toast.error("Failed to fetch resources");
      }
      setHasMore(false);
      if (isReset) setCategorizedData([]);
    }
  };

  useEffect(() => {
    loadResources(page, page === 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fileType, categoryId]);

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
    setCategorizedData([]);
    setHasMore(true);
  };


  const ResourceCard = ({ resource }) => {
    const hasFiles = resource.files && resource.files.length > 0;
    const hasVideo = !!resource.youtubeLink;

    let totalViews = 0;
    let videoViews = 0;
    if (hasVideo) {
      videoViews += resource.youtubeViewCount || 0;
    }
    if (hasFiles) {
      totalViews += resource.files.reduce((sum, f) => sum + (f.viewCount || 0), 0);
    }
    const totalDownloads = hasFiles ? resource.files.reduce((sum, f) => sum + (f.downloadCount || 0), 0) : 0;

    return (
      <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', transition: '0.3s', '&:hover': { boxShadow: 4, borderColor: 'primary.main' } }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" alignItems="flex-start" mb={2}>
            <Box mr={2} display="flex" alignItems="center" justifyContent="center" width={48} height={48} borderRadius={2} borderColor="primary" border='2' color="primary.main">
              {hasVideo ? <PlayCircleOutlineIcon fontSize="large" /> : (hasFiles ? <DescriptionIcon fontSize="large" /> : <AttachFileIcon fontSize="large" />)}
            </Box>
            <Box overflow="hidden" flex={1}>
              <Typography variant="h6" fontWeight="bold" noWrap title={resource.name} sx={{ mb: 0.5 }}>
                {resource.name}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {hasVideo && <Chip size="small" label="Video" color="error" variant="outlined" />}
                {hasFiles && <Chip size="small" label={`${resource.files.length} File${resource.files.length > 1 ? 's' : ''}`} color="primary" variant="outlined" />}
              </Box>
            </Box>
          </Box>

          <Box display="flex" gap={3} mb={2}>
            {hasVideo && (
              <Typography variant="body2" color="text.secondary" display="flex" alignItems="center">
                <VideocamIcon fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} /> {videoViews}
              </Typography>)
            }
            <Typography variant="body2" color="text.secondary" display="flex" alignItems="center">
              <VisibilityIcon fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} /> {totalViews}
            </Typography>
            {hasFiles && (
              <Typography variant="body2" color="text.secondary" display="flex" alignItems="center">
                <DownloadIcon fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} /> {totalDownloads}
              </Typography>
            )}
          </Box>

          {resource?.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              dangerouslySetInnerHTML={{ __html: formatDescription(resource.description) }}
            />
          )}
        </CardContent>
        <CardActions sx={{ p: 2, pt: 0, mt: 'auto' }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => navigate(`../view-resource/${resource._id}`)}
          >
            View Resource
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box>
      <div className="pt-5">
        <div className="chapter-heading">
          <h2 className="text-center title-font text-light py-3 mt-1">Resources</h2>
        </div>
      </div>

      <Box p={{ xs: 2, md: 5 }} className="overallPadding">
        <Box display="flex" justifyContent={'end'} gap={3} mb={5} flexWrap="wrap">
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel shrink={true}>Category</InputLabel>
            <Select
              value={categoryId}
              label="Category"
              displayEmpty={true}
              onChange={handleFilterChange(setCategoryId)}
            >
              <MenuItem value=""><em>All Categories</em></MenuItem>
              {categoryData?.data?.map(cat => (
                <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel shrink={true}>File Type</InputLabel>
            <Select
              value={fileType}
              label="File Type"
              displayEmpty={true}
              onChange={handleFilterChange(setFileType)}
            >
              <MenuItem value=""><em>All Types</em></MenuItem>
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="image">Image</MenuItem>
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="docx">Document (Word)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {categorizedData.length === 0 && !isLoading && !isFetching && (
          <Typography variant="h6" color="text.secondary" align="center" mt={5}>
            Will be added shortly
          </Typography>
        )}

        {categorizedData.map((categoryGroup, index) => {
          const isLastCategory = index === categorizedData.length - 1;
          return (
            <Box key={categoryGroup.categoryDetails?._id || `uncategorized-${index}`} mb={5}>
              <Typography variant="h5" fontWeight="bold" color="primary" mb={1}>
                {categoryGroup.categoryDetails?.name || 'Uncategorized'}
              </Typography>
              {categoryGroup.categoryDetails?.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  mb={3}
                  dangerouslySetInnerHTML={{ __html: formatDescription(categoryGroup.categoryDetails.description, 100) }}
                />
              )}

              <Grid container spacing={3}>
                {categoryGroup.resources.map((resource, resIndex) => {
                  const isLastResource = isLastCategory && resIndex === categoryGroup.resources.length - 1;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={resource._id} ref={isLastResource ? lastElementRef : null}>
                      <ResourceCard resource={resource} />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        })}

        {(isLoading || isFetching) && (
          <Box mt={3}>
            <Typography variant="h5" mb={2}><Skeleton width="30%" /></Typography>
            <Grid container spacing={3}>
              {[1, 2, 3].map(i => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box display="flex" mb={2}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                        <Box width="100%">
                          <Skeleton width="80%" />
                          <Skeleton width="60%" />
                        </Box>
                      </Box>
                      <Skeleton width="40%" />
                    </CardContent>
                    <CardActions sx={{ borderTop: '1px solid #f0f0f0' }}>
                      <Skeleton variant="rounded" width={80} height={30} sx={{ mr: 1 }} />
                      <Skeleton variant="rounded" width={80} height={30} />
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
}
